import React, { useState, useEffect } from 'react';
import { 
  Bus, ArrowLeft, Check, Plane, Building2, MapPin, Star,
  RefreshCw, AlertTriangle, ShoppingCart
} from 'lucide-react';
import { Button } from './ui/button';
import { useCurrency } from '../context/CurrencyContext';
import { getVisaOptions } from '../services/umrahApi';

// Vehicle icons mapping
const vehicleIcons = {
  car: '🚗',
  h1van: '🚐',
  hiace: '🚐',
  gmc: '🚌',
  coaster: '🚌'
};

const TransportZiyarat = ({ 
  searchParams,
  selectedPackageData,
  onContinue,
  onBack
}) => {
  const { formatPrice } = useCurrency();
  
  // Loading and validation states
  const [loading, setLoading] = useState(true);
  const [revalidating, setRevalidating] = useState(false);
  const [priceChanged, setPriceChanged] = useState(false);
  const [originalPricing, setOriginalPricing] = useState(null);
  
  // Transport options state
  const [transportOptions, setTransportOptions] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [visaOptions, setVisaOptions] = useState([]);
  const [selectedVisa, setSelectedVisa] = useState(null);
  
  // Dynamic routes based on itinerary
  const [dynamicRoutes, setDynamicRoutes] = useState([]);
  const [selectedTransfers, setSelectedTransfers] = useState({});
  
  // Revalidated package data
  const [validatedPackage, setValidatedPackage] = useState(selectedPackageData);
  
  // Ziyarat tours selection
  const [selectedZiyarat, setSelectedZiyarat] = useState({
    makkah: false,
    madinah: false
  });
  
  const passengers = (searchParams?.adults || 2) + (searchParams?.children || 0) + (searchParams?.infants || 0);
  const makkahNights = searchParams?.makkahNights || 5;
  const madinahNights = searchParams?.madinahNights || 3;
  
  // Determine arrival and departure airports from search params
  const arrivalAirport = searchParams?.arrivalAirport || 'JED';
  const departureAirport = searchParams?.returnFrom || 'MED';
  const visitMakkah = searchParams?.makkahNights > 0;
  const visitMadinah = searchParams?.madinahNights > 0;
  
  // Ziyarat tours configuration
  const ziyaratTours = [
    { 
      id: 'makkah', 
      name: 'Makkah Ziyarat Tour', 
      location: 'Makkah',
      description: 'Visit Cave of Hira, Cave of Thawr, Arafat, Muzdalifah, Mina',
      duration: '6 hours',
      priceKey: 'makkah_ziyarat'
    },
    { 
      id: 'madinah', 
      name: 'Medina Ziyarat Tour', 
      location: 'Medina',
      description: 'Visit Quba Mosque, Uhud Mountain, Qiblatain Mosque, Date Farms',
      duration: '5 hours',
      priceKey: 'madinah_ziyarat'
    }
  ];

  // Generate dynamic transfer routes based on itinerary
  useEffect(() => {
    const routes = [];
    const initialSelections = {};

    // Arrival transfer
    if (arrivalAirport === 'JED' && visitMakkah) {
      routes.push({ 
        id: 'jeddahMakkah', 
        name: 'Jeddah Airport → Makkah Hotel', 
        icon: '✈️', 
        key: 'jeddah_makkah',
        required: true 
      });
      initialSelections.jeddahMakkah = true;
    } else if (arrivalAirport === 'MED' && visitMadinah) {
      routes.push({ 
        id: 'madinahArrival', 
        name: 'Madinah Airport → Madinah Hotel', 
        icon: '✈️', 
        key: 'madinah_arrival',
        required: true 
      });
      initialSelections.madinahArrival = true;
    }

    // Inter-city transfer
    if (visitMakkah && visitMadinah) {
      routes.push({ 
        id: 'makkahMadinah', 
        name: 'Makkah → Madinah Hotel', 
        icon: '🕌', 
        key: 'makkah_madinah',
        required: true 
      });
      initialSelections.makkahMadinah = true;
    }

    // Departure transfer
    if (departureAirport === 'JED') {
      if (visitMadinah) {
        routes.push({ 
          id: 'madinahJeddah', 
          name: 'Madinah → Jeddah Airport', 
          icon: '🏨', 
          key: 'madinah_jeddah',
          required: true 
        });
        initialSelections.madinahJeddah = true;
      } else if (visitMakkah) {
        routes.push({ 
          id: 'makkahJeddah', 
          name: 'Makkah → Jeddah Airport', 
          icon: '🏨', 
          key: 'makkah_jeddah',
          required: true 
        });
        initialSelections.makkahJeddah = true;
      }
    } else if (departureAirport === 'MED' && visitMadinah) {
      routes.push({ 
        id: 'madinahAirport', 
        name: 'Madinah Hotel → Madinah Airport', 
        icon: '🛫', 
        key: 'madinah_airport',
        required: true 
      });
      initialSelections.madinahAirport = true;
    }

    setDynamicRoutes(routes);
    setSelectedTransfers(initialSelections);
  }, [arrivalAirport, departureAirport, visitMakkah, visitMadinah]);

  // Revalidate and fetch fresh data on mount
  useEffect(() => {
    revalidatePackage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const revalidatePackage = async () => {
    setRevalidating(true);
    setLoading(true);
    
    try {
      // Store original pricing for comparison
      if (selectedPackageData?.pricing) {
        setOriginalPricing(selectedPackageData.pricing);
      }
      
      // Use mock transport options for now
      const mockTransportOptions = [
        {
          id: 'car',
          name: 'Private Car',
          vehicle_type: 'car',
          min_passengers: 1,
          max_passengers: 4,
          standard_package_price: 150,
          is_recommended: passengers <= 4,
          route_prices: {
            jeddah_makkah: 50,
            makkah_madinah: 100,
            madinah_jeddah: 103,
            madinah_airport: 35,
            makkah_jeddah: 120,
            madinah_arrival: 35
          }
        },
        {
          id: 'h1van',
          name: 'H1 Van',
          vehicle_type: 'h1van',
          min_passengers: 5,
          max_passengers: 8,
          standard_package_price: 200,
          is_recommended: passengers >= 5 && passengers <= 8,
          route_prices: {
            jeddah_makkah: 70,
            makkah_madinah: 120,
            madinah_jeddah: 123,
            madinah_airport: 45,
            makkah_jeddah: 140,
            madinah_arrival: 45
          }
        },
        {
          id: 'hiace',
          name: 'Toyota Hiace',
          vehicle_type: 'hiace',
          min_passengers: 9,
          max_passengers: 14,
          standard_package_price: 250,
          is_recommended: passengers >= 9 && passengers <= 14,
          route_prices: {
            jeddah_makkah: 90,
            makkah_madinah: 140,
            madinah_jeddah: 143,
            madinah_airport: 55,
            makkah_jeddah: 160,
            madinah_arrival: 55
          }
        }
      ];
      
      setTransportOptions(mockTransportOptions);
      
      // Auto-select recommended vehicle
      const recommended = mockTransportOptions.find(v => v.is_recommended) || mockTransportOptions[0];
      if (recommended) {
        setSelectedVehicle(recommended);
      }
      
      // Fetch visa options
      const visas = await getVisaOptions();
      setVisaOptions(visas || []);
      
      // Use existing package data without re-fetching (to avoid rate limiting)
      // Price revalidation will happen at checkout time
      setValidatedPackage(selectedPackageData);
      
    } catch (error) {
      console.error('Revalidation error:', error);
      // Still allow user to proceed with existing data
      setValidatedPackage(selectedPackageData);
    } finally {
      setRevalidating(false);
      setLoading(false);
    }
  };

  // Calculate transport cost based on selections
  const calculateTransportCost = () => {
    if (!selectedVehicle) return 0;
    
    let total = 0;
    const prices = selectedVehicle.route_prices || {};
    
    // Calculate based on dynamic routes
    dynamicRoutes.forEach(route => {
      if (selectedTransfers[route.id]) {
        total += prices[route.key] || 0;
      }
    });
    
    return total;
  };
  
  // Calculate ziyarat cost
  const calculateZiyaratCost = () => {
    let total = 0;
    const ziyaratPrices = selectedVehicle?.ziyarat_prices || { makkah_ziyarat: 52, madinah_ziyarat: 52 };
    
    if (selectedZiyarat.makkah) total += ziyaratPrices.makkah_ziyarat || 52;
    if (selectedZiyarat.madinah) total += ziyaratPrices.madinah_ziyarat || 52;
    return total;
  };
  
  // Calculate total package price
  const calculateTotal = () => {
    const flightCost = validatedPackage?.pricing?.flights || 0;
    const makkahCost = (validatedPackage?.makkahHotel?.price_per_night || 0) * makkahNights * passengers;
    const madinahCost = (validatedPackage?.madinahHotel?.price_per_night || 0) * madinahNights * passengers;
    const transportCost = calculateTransportCost();
    const ziyaratCost = calculateZiyaratCost();
    const visaCost = selectedVisa?.price || 0;
    
    const subtotal = flightCost + makkahCost + madinahCost + transportCost + ziyaratCost + visaCost;
    const serviceFee = Math.round(subtotal * 0.05 * 100) / 100;
    const total = subtotal + serviceFee;
    
    return { subtotal, serviceFee, total };
  };
  
  const { subtotal, serviceFee, total } = calculateTotal();

  // Handle continue to booking
  const handleContinue = () => {
    const finalPackage = {
      ...validatedPackage,
      transport: {
        vehicle: selectedVehicle,
        transfers: selectedTransfers,
        cost: calculateTransportCost()
      },
      ziyarat: {
        tours: selectedZiyarat,
        cost: calculateZiyaratCost()
      },
      visa: selectedVisa,
      pricing: {
        ...validatedPackage?.pricing,
        transport: calculateTransportCost(),
        ziyarat: calculateZiyaratCost(),
        visa: selectedVisa?.price || 0,
        subtotal,
        serviceFee,
        total
      }
    };
    
    onContinue(finalPackage);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 flex items-center justify-center">
        <div className="text-center text-white">
          <RefreshCw className="w-16 h-16 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Validating Your Package...</h2>
          <p className="text-purple-100">Checking availability and latest prices</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex flex-col lg:flex-row">
        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bus className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-xl font-bold text-white">Transport & Ziyarat</h1>
                <p className="text-purple-200 text-sm">Choose your transportation and religious tours</p>
              </div>
            </div>
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-white hover:text-purple-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
          
          {/* Price Change Alert */}
          {priceChanged && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Price Update</h3>
                <p className="text-amber-700 text-sm">
                  Some prices have changed since your initial search. The updated prices are shown in your package summary.
                </p>
              </div>
            </div>
          )}
          
          {/* Select Vehicle Type */}
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bus className="w-5 h-5 text-white" />
                <h2 className="text-lg font-bold text-white">Select Vehicle Type</h2>
              </div>
              <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full">
                {passengers} passengers → {selectedVehicle?.name || 'Select'}
              </span>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {transportOptions.map((vehicle) => (
                <button
                  key={vehicle.id}
                  onClick={() => setSelectedVehicle(vehicle)}
                  className={`relative bg-white rounded-xl p-3 text-center transition-all ${
                    selectedVehicle?.id === vehicle.id 
                      ? 'ring-2 ring-purple-400 shadow-lg' 
                      : 'hover:shadow-md'
                  }`}
                >
                  {vehicle.is_recommended && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Recommended
                    </span>
                  )}
                  <div className="text-3xl mb-1">{vehicleIcons[vehicle.vehicle_type] || '🚗'}</div>
                  <div className="font-semibold text-gray-800 text-sm">{vehicle.name}</div>
                  <div className="text-xs text-gray-500">{vehicle.min_passengers}-{vehicle.max_passengers} passengers</div>
                  <div className="text-purple-600 font-bold mt-1">£{vehicle.standard_package_price}</div>
                  {selectedVehicle?.id === vehicle.id && (
                    <div className="text-green-600 text-xs mt-1 flex items-center justify-center gap-1">
                      <Check className="w-3 h-3" /> Selected
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {/* Transfer Routes */}
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-white" />
                <h2 className="text-lg font-bold text-white">Transfer Routes</h2>
              </div>
              <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full">
                {dynamicRoutes.filter(r => r.required).length > 0 ? 'Required' : 'Optional'}
              </span>
            </div>
            <p className="text-pink-100 text-sm mb-3">
              {dynamicRoutes.filter(r => r.required).length > 0 
                ? 'Required transfers based on your itinerary' 
                : 'Select the transfers you need (costs included in total)'}
            </p>
            
            <div className="space-y-2">
              {dynamicRoutes.map((route) => {
                const price = selectedVehicle?.route_prices?.[route.key] || 0;
                const isSelected = selectedTransfers[route.id];
                
                return (
                  <button
                    key={route.id}
                    onClick={() => !route.required && setSelectedTransfers(prev => ({...prev, [route.id]: !prev[route.id]}))}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      isSelected 
                        ? 'bg-white shadow-md' 
                        : 'bg-white/80 hover:bg-white'
                    } ${route.required ? 'opacity-75 cursor-not-allowed' : ''}`}
                    disabled={route.required}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <span className="text-xl">{route.icon}</span>
                      <div className="text-left">
                        <span className="font-medium text-gray-800 block">{route.name}</span>
                        {route.required && (
                          <span className="text-xs text-gray-500">Required for your itinerary</span>
                        )}
                      </div>
                    </div>
                    <div className={`font-bold ${isSelected ? 'text-green-600' : 'text-gray-500'}`}>
                      £{price.toFixed(2)} {isSelected && <Check className="w-4 h-4 inline" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Optional Ziyarat Tours */}
          <div className="bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-white" />
              <h2 className="text-lg font-bold text-white">Optional Ziyarat Tours</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-3">
              {ziyaratTours.map((tour) => {
                const isSelected = selectedZiyarat[tour.id];
                const price = selectedVehicle?.ziyarat_prices?.[tour.priceKey] || 52;
                
                return (
                  <button
                    key={tour.id}
                    onClick={() => setSelectedZiyarat(prev => ({...prev, [tour.id]: !prev[tour.id]}))}
                    className={`relative bg-white rounded-xl p-4 text-left transition-all ${
                      isSelected ? 'ring-2 ring-emerald-400 shadow-lg' : 'hover:shadow-md'
                    }`}
                  >
                    <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-emerald-500' : 'bg-gray-200'
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-gray-800">{tour.name}</h3>
                      <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                        {tour.location}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{tour.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">⏱ {tour.duration}</span>
                      <span className="text-emerald-600 font-bold">£{price}.00/vehicle</span>
                    </div>
                  </button>
                );
              })}
            </div>
            
            <p className="text-emerald-100 text-sm mt-3">
              {Object.values(selectedZiyarat).filter(Boolean).length} tour(s) selected
            </p>
          </div>
          
          {/* Visa Options */}
          <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">📋</span>
              <h2 className="text-lg font-bold text-gray-800">Visa Services</h2>
              <span className="bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full ml-auto">Optional</span>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-3">
              {visaOptions.map((visa) => (
                <button
                  key={visa.id}
                  onClick={() => setSelectedVisa(selectedVisa?.id === visa.id ? null : visa)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    selectedVisa?.id === visa.id 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <h3 className="font-semibold text-gray-800">{visa.name}</h3>
                  <p className="text-xs text-gray-500 mb-2">{visa.processing_time}</p>
                  <p className="text-purple-600 font-bold">£{visa.price}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Package Summary Sidebar */}
        <div className="lg:w-96 p-4 lg:p-6 bg-white lg:border-l">
          <div className="sticky top-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Your Package
            </h2>
            
            <div className="space-y-3">
              {/* Outbound Flight */}
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="flex items-center gap-2 text-gray-800 font-medium">
                    <Plane className="w-4 h-4" />
                    Outbound
                  </div>
                  <p className="text-xs text-gray-500">
                    {validatedPackage?.outboundFlight?.validating_carrier || 'SV'} - {searchParams?.departureAirport?.code || 'LON'} → {searchParams?.arrivalAirport || 'JED'}
                  </p>
                </div>
                <span className="font-bold text-gray-800">
                  £{(validatedPackage?.outboundFlight?.pricing?.total_fare || validatedPackage?.pricing?.flights / 2 || 350).toFixed(2)}
                </span>
              </div>
              
              {/* Return Flight */}
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="flex items-center gap-2 text-gray-800 font-medium">
                    <Plane className="w-4 h-4 rotate-180" />
                    Return
                  </div>
                  <p className="text-xs text-gray-500">
                    {validatedPackage?.inboundFlight?.validating_carrier || 'SV'} - {searchParams?.returnFrom || 'MED'} → {searchParams?.departureAirport?.code || 'LON'}
                  </p>
                </div>
                <span className="font-bold text-gray-800">
                  £{(validatedPackage?.inboundFlight?.pricing?.total_fare || validatedPackage?.pricing?.flights / 2 || 350).toFixed(2)}
                </span>
              </div>
              
              {/* Makkah Hotel */}
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="flex items-center gap-2 text-gray-800 font-medium">
                    <Building2 className="w-4 h-4" />
                    Makkah Hotel
                  </div>
                  <p className="text-xs text-gray-500">
                    {validatedPackage?.makkahHotel?.name?.substring(0, 25) || 'Hotel'} • {makkahNights} nights
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-800">
                    £{((validatedPackage?.makkahHotel?.price_per_night || 0) * makkahNights * passengers).toFixed(2)}
                  </span>
                  {validatedPackage?.makkahHotel?.original_price && (
                    <p className="text-xs text-amber-600">
                      Was £{(validatedPackage.makkahHotel.original_price * makkahNights * passengers).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Madinah Hotel */}
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="flex items-center gap-2 text-gray-800 font-medium">
                    <Building2 className="w-4 h-4" />
                    Madinah Hotel
                  </div>
                  <p className="text-xs text-gray-500">
                    {validatedPackage?.madinahHotel?.name?.substring(0, 25) || 'Hotel'} • {madinahNights} nights
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-800">
                    £{((validatedPackage?.madinahHotel?.price_per_night || 0) * madinahNights * passengers).toFixed(2)}
                  </span>
                  {validatedPackage?.madinahHotel?.original_price && (
                    <p className="text-xs text-amber-600">
                      Was £{(validatedPackage.madinahHotel.original_price * madinahNights * passengers).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Transport */}
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="flex items-center gap-2 text-gray-800 font-medium">
                    <Bus className="w-4 h-4" />
                    Transport
                  </div>
                  <p className="text-xs text-gray-500">
                    {selectedVehicle?.name || 'Select vehicle'}
                  </p>
                </div>
                <span className="font-bold text-gray-800">£{calculateTransportCost().toFixed(2)}</span>
              </div>
              
              {/* Ziyarat Tours (if selected) */}
              {calculateZiyaratCost() > 0 && (
                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <div className="flex items-center gap-2 text-gray-800 font-medium">
                      <Star className="w-4 h-4" />
                      Ziyarat Tours
                    </div>
                  </div>
                  <span className="font-bold text-gray-800">£{calculateZiyaratCost().toFixed(2)}</span>
                </div>
              )}
              
              {/* Visa (if selected) */}
              {selectedVisa ? (
                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <div className="flex items-center gap-2 text-gray-800 font-medium">
                      📋 Visa
                    </div>
                  </div>
                  <span className="font-bold text-gray-800">£{selectedVisa.price.toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex items-center justify-between py-2 border-b text-gray-400">
                  <div className="flex items-center gap-2">
                    📋 Visa
                  </div>
                  <span className="text-sm">Optional</span>
                </div>
              )}
            </div>
            
            {/* Totals */}
            <div className="mt-4 pt-4 border-t space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>£{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Service Fee (5%)</span>
                <span>£{serviceFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-800 pt-2 border-t">
                <span>Total</span>
                <span className="text-emerald-600">£{total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500 text-right">
                Deposit: £{(50 * passengers).toFixed(2)} (£50/person)
              </p>
            </div>
            
            {/* Continue Button */}
            <Button
              onClick={handleContinue}
              className="w-full mt-4 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              data-testid="continue-to-booking-btn"
            >
              <ShoppingCart className="w-5 h-5" />
              Continue to Booking
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransportZiyarat;
